import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { useDispatch } from 'react-redux';
import { setFlowToken, setPhone, setSelectedContactToken } from '../../store/mainSlice';
import FlowLayout from '../FlowLayout';
import { useA11yTokens } from '../../hooks/useA11yTokens';
import { useDirection } from '../../hooks/useDirection';
import { useIsDesktopDevice } from '../../hooks/useIsDesktopDevice';

const Companies: React.FC = () => {
    const { t, i18n } = useTranslation();
    const theme4dTokens = useA11yTokens();
    const isDesktop = useIsDesktopDevice();
    const { isLTR } = useDirection();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { flowToken, customersContacts, isLoading, packageToken, env } = useAppSelector((state) => state.main);

    React.useEffect(() => {
        if (!flowToken || customersContacts.length === 0) {
            dispatch(setPhone(''));
            dispatch(setFlowToken(''));
            navigate(packageToken ? `/?token=${encodeURIComponent(packageToken)}&ENV=${env}` : '/');
        }
    }, []);

    const onPick = (contactToken: string) => {
        dispatch(setSelectedContactToken(contactToken));
        navigate('/offer');
    };

    return (
        <FlowLayout>
            <Typography sx={{ fontSize: isDesktop ? 26 : 24, fontWeight: 800, color: theme4dTokens.text, wordBreak: 'break-word', ...(isDesktop && { mt: '50px' }) }}>
                {t('companies.title')}
            </Typography>
            <Typography sx={{ fontSize: isDesktop ? 17 : 15, color: theme4dTokens.muted, wordBreak: 'break-word', ...(isDesktop && { mt: '16px' }) }}>
                {t('companies.subtitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', ...(isDesktop && { mt: '20px' }) }}>
                {customersContacts.map((c) => (
                    <Button
                        key={c.contactToken}
                        variant="outlined"
                        disabled={isLoading}
                        onClick={() => onPick(c.contactToken)}
                        sx={{
                            justifyContent: 'space-between',
                            textAlign: 'right',
                            bgcolor: theme4dTokens.surface,
                            borderColor: theme4dTokens.border,
                            borderRadius: theme4dTokens.radius,
                            p: '15px 16px',
                            color: theme4dTokens.text,
                            '&:hover': {
                                borderColor: theme4dTokens.accent,
                                bgcolor: theme4dTokens.surface,
                                boxShadow: `0 0 0 3px ${theme4dTokens.accentSoft}`,
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 18 : 16, fontWeight: 700, color: theme4dTokens.text, wordBreak: 'break-word' }}>
                                {c.customerName}
                            </Typography>
                            <Typography sx={{ fontSize: isDesktop ? 14.5 : 12.5, color: theme4dTokens.muted, textAlign: isLTR ? 'left' : 'right', wordBreak: 'break-word' }}>{c.contactName}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: isDesktop ? 24 : 22, fontWeight: 700, color: theme4dTokens.accent, lineHeight: 1, flexShrink: 0 }}>
                            {'›'}
                        </Typography>
                    </Button>
                ))}
            </Box>
        </FlowLayout>
    );
};

export default Companies;
